import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ApiStorage from '../../redux/ApiStorage';
import ParkingAuthActions from '../../services/ParkingAuthActions';
import NotificationActions from '../../services/NotificationActions';
import { getFcmToken } from '../../firebase/firebase';
import useSocket from '../../socket/useSocket';

const MOBILE_AUTH_STORAGE_KEY = 'mobileAttendanceLoggedIn';
const NOTIFICATIONS_POLL_MS = 3 * 60 * 1000;
// لما الأدمن يبعت إشعار، كل الأجهزة المفتوحة بتعرف من السوكيت.
// كل جهاز بيستنى وقت عشوائي قبل ما يطلب القائمة، لحتى ما يضربوا السيرفر كلهم بنفس الثانية.
const SOCKET_REFRESH_MAX_DELAY_MS = 15 * 1000;

const readInitialAuth = () => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return localStorage.getItem(MOBILE_AUTH_STORAGE_KEY) === '1';
};

const MobileAppContext = createContext(null);

export const MobileAppProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(readInitialAuth);

  // الإشعارات بتنكتب من لوحة الأدمن، وحالة "مقروء" محفوظة على الجهاز.
  const [notificationItems, setNotificationItems] = useState([]);
  const [readIds, setReadIds] = useState(() => NotificationActions.getReadIds());
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const fetchingRef = useRef(false);
  const { lastMessage } = useSocket();

  const setLoggedIn = useCallback((value) => {
    setIsLoggedIn(value);
    if (typeof window !== 'undefined' && window.localStorage) {
      if (value) {
        localStorage.setItem(MOBILE_AUTH_STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(MOBILE_AUTH_STORAGE_KEY);
      }
    }
  }, []);

  const login = useCallback(
    async (phoneNumber) => {
      const deviceKey = await getFcmToken();
      const result = await ParkingAuthActions.login(phoneNumber, deviceKey);
      if (result?.success) {
        ApiStorage.setCurrentUser(result.data);
        setLoggedIn(true);
      }
      return result;
    },
    [setLoggedIn]
  );

  const register = useCallback(
    async (saveData) => {
      const deviceKey = await getFcmToken();
      const result = await ParkingAuthActions.register(saveData, deviceKey);
      if (result?.success) {
        ApiStorage.setCurrentUser(result.data);
        setLoggedIn(true);
      }
      return result;
    },
    [setLoggedIn]
  );

  const updateProfile = useCallback(async (saveData) => {
    const deviceKey = ApiStorage.getDeviceKey() || (await getFcmToken());
    const result = await ParkingAuthActions.updateProfile(saveData, deviceKey);
    if (result?.success && result.data) {
      ApiStorage.setCurrentUser({
        ...ApiStorage.getCurrentUser(),
        ...result.data,
      });
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    const deviceKey = ApiStorage.getDeviceKey() || (await getFcmToken().catch(() => ''));
    ParkingAuthActions.logout(deviceKey).catch(() => {});
    setIsLoggedIn(false);
    setNotificationItems([]);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(MOBILE_AUTH_STORAGE_KEY);
    }
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setNotificationsLoading(true);
    try {
      const result = await NotificationActions.listForMobile();
      if (result?.success) {
        setNotificationItems(NotificationActions.extractList(result));
      }
    } catch (e) {
      // بدون نت: منخلّي آخر قائمة متل ما هي
    } finally {
      fetchingRef.current = false;
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    refreshNotifications();
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        refreshNotifications();
      }
    }, NOTIFICATIONS_POLL_MS);
    return () => clearInterval(timer);
  }, [isLoggedIn, refreshNotifications]);

  useEffect(() => {
    if (!isLoggedIn || lastMessage?.type !== 'notification') return undefined;
    const timer = setTimeout(
      refreshNotifications,
      Math.random() * SOCKET_REFRESH_MAX_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [isLoggedIn, lastMessage, refreshNotifications]);

  const markAllNotificationsRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      notificationItems.forEach((item) => {
        const id = NotificationActions.getId(item);
        if (id) next.add(id);
      });
      NotificationActions.saveReadIds(next);
      return next;
    });
  }, [notificationItems]);

  const notifications = useMemo(
    () =>
      notificationItems.map((item) => ({
        raw: item,
        idKey: NotificationActions.getId(item),
        read: readIds.has(NotificationActions.getId(item)),
      })),
    [notificationItems, readIds]
  );

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({
      isLoggedIn,
      login,
      register,
      updateProfile,
      logout,
      setLoggedIn,
      notifications,
      notificationsLoading,
      unreadCount,
      refreshNotifications,
      markAllNotificationsRead,
    }),
    [
      isLoggedIn,
      login,
      register,
      updateProfile,
      logout,
      setLoggedIn,
      notifications,
      notificationsLoading,
      unreadCount,
      refreshNotifications,
      markAllNotificationsRead,
    ]
  );

  return (
    <MobileAppContext.Provider value={value}>
      {children}
    </MobileAppContext.Provider>
  );
};

export const useMobileApp = () => {
  const ctx = useContext(MobileAppContext);
  if (!ctx) {
    throw new Error('useMobileApp must be used within MobileAppProvider');
  }
  return ctx;
};
